// tableMain.tsx
import './tableMain.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TableBody from './table_body/tableBody';
import TableTools from './table_tools/tableTools';
import TableSummary, { SummaryConfig } from './table_summary/tableSummary';
import { useDataManager } from '../../contexts/dataManager';
import { useMonthNavigation } from '../../contexts/monthNavigationContext';
import { getHoustonDateInfo } from '../../helper_functions';
import { applyMonthFilter, evaluateWarningRules, applyWarningFilters, applyColumnFilters, applySorting, WarningTypes } from './dataProcessing';
import { useApiData } from '../../contexts/apiService';
import { UserRole } from '../../contexts/userAccess';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { InputColumnConfig } from '../common/table_input/tableInput';
import TableSidePanel from './table_sidepanel/tableSidepanel';
import { getSavedTableState, saveTableState } from './table_filter/tableFilterPersistance';
import { trackEvent } from '../../contexts/userAnalytics';

// interface for general table properties
export interface SQLTableProps {
    tableType?: 'sql_default' | 'firebase'; // whether to use default sql table or firebase table - firebase enabled makes edit on user documents instead of a sql table
    tableData: Record<string, any>[];
    idColumn: string; // actual sql id column name
    tableName: string; // actual sql table name
    columnConfigs: TableColumnConfig[];
    jsonColumnConfig?: JsonColumnConfig[];
    showHeader?: boolean; // if true, show table header
    tableTools?: boolean; // if true, show buttons for delete, export, refresh, clear filters
    sortBy?: string[]; // columns to sort by default
    sortOrder?: Record<string, 'asc' | 'desc'>;
    enableSummary?: boolean; // if true, show summary row at bottom
    summaryConfig?: SummaryConfig[]; // configuration for table summary
    // utility parameters
    preFilterData?: { column: string; value?: any }; // if provided, this will hide data from the user based on the filtered column
    persistFilters?: boolean;
    initialFilter?: { column: string; value: any } // allows pre-filtering table on load - can be unfiltered by user
    initialMonthYear?: string; // initial month to use for data fetch, bypasses context timing issue
    monthNavigation?: boolean; // if true, enable month navigation for table
    selectableRows?: boolean; // if true, rows can be selected
    multipleSelection?: boolean; // allows multiple rows to be selected
    deletableRows?: boolean; // if true, rows can be deleted
    deletionRequiresApproval?: boolean; // if true, deletions go through approval flow instead of direct delete
    enableFilters?: boolean; // if true, enable column filters
    downloadable?: boolean; // if true, table can be downloaded
    exportColumnOrder?: string[]; // column order for the Excel download (unlisted columns go after, in table order)
    refreshable?: boolean; // if true, table can be refreshed
    enableSettings?: boolean; // if true, enable table settings
    enableColumnConfiguration?: boolean; // if true, enable column configuration settings
    enableWarnings?: boolean; // if true, enable table warnings
    warningRules?: any[]; // rules for warnings
    userRoleLevel?: number;
    userData?: any; // User data object for isVisible column filtering
    onRowClick?: (rowData: any) => void; // callback when a row is clicked
    // Side panel — renders as a flex sibling using VacTruckSidePanel-style slide-in
    sidepanelContent?: (panelData: { rowData: any; colName: string; tableName: string }, onClose: () => void) => React.ReactNode;
    sidepanelWidth?: number; // px, default 480
    pageSize?: number; // rows per page, applied after all filters (0 = no pagination)
    // style parameters
    rowHeight?: number;
    rowRibbon?: {condition: (rowData: any) => boolean; color: string}; // Adds a small colored rectangle at row start
    rowColor?: Array<{condition: (rowData: any) => boolean; color: string}>; // Adds a background color to the ro
    showTable?: boolean;
    rowActions?: RowActionConfig[]; // Array of action configurations
    rowActionsPosition?: 'start' | 'end'; // Where to render action column
    rowActionsWidth?: number; // Width of actions column
    skipDataFetch?: boolean; // if true, skip backend fetch (use tableData prop directly)
}

// Interface for row action configurations
export interface RowActionConfig {
    id: string; // Unique identifier (e.g., 'approve', 'deny', 'acknowledge')
    label: string; // Button text
    icon?: React.ReactNode; // Optional icon
    variant?: 'primary' | 'danger' | 'success' | 'warning'; // Button styling
    onClick: (rowData: any, rowId: any, reason?: string) => void | Promise<void>; // Action handler with optional reason
    isVisible?: (rowData: any) => boolean; // Conditional visibility
    isDisabled?: (rowData: any) => boolean; // Conditional disable state
    requiresConfirmation?: boolean; // Show confirmation dialog
    confirmationConfig?: {
        title: string;
        message: string | ((rowData: any) => string);
        requiresReason?: boolean | ((rowData: any) => boolean); // Show text input for reason (can be conditional)
    };
    minRoleLevel?: number; // Minimum user role level required
}

// interface to define the structure of table columns
export interface TableColumnConfig {
    colName: string; // table edits are by default done by this column name
    actualColName?: string; // if colName is different from actual sql column name
    subTable?: string; // if this column is from another table
    colType: 'text' | 'number' | 'boolean' | 'json' | 'coordinate' | 'date' | 'datetime' | 'accounting' | 'month' | 'calculated';
    textValues?: boolean; // for boolean columns, if true, use text values instead of true/false
    noAsNull?: boolean; // for boolean columns, if true, display 0 as null/empty
    decimals?: number; // for number columns, how many decimals to show
    redNegative?: boolean; // for number/accounting columns, if true, show negative numbers in red
    colHeader?: string;
    editable?: boolean | ((rowData: any) => boolean);
    rolesNotAllowedToEdit?: UserRole[]; // Forced: Array of user roles not allowed to edit this column.
    inputType?: 'input' | 'dropdown' | 'typeahead';
    enableSidepanel?: boolean; // for table columns, if true, show side panel icon
    dropdownOptions?: Array<{ value: any; label: string; [key: string]: any }> // Options for dropdowns, optional key
    jsonCols?: string[]; // specify all fields in json col
    dependsOn?: string; // Column name dropdown depends on
    getFilteredOptions?: (dependentValue: any, allOptions: Array<{ value: any; label: string; [key: string]: any }>, rowData?: any) => Array<{ value: any; label: string; [key: string]: any }>;
    editableLevel?: number; // Minimum user role level required to edit this column
    requiresApproval?: boolean; // If true, changes to this column require approval
    approvalLevel?: number; // Minimum user role level required to approve changes to this column
    isVisible?: (userData: any) => boolean; // Function to determine if column should be visible
    conditionalDisplay?: (value: any, rowData: any, userData: any) => any; // Transform display value based on permissions
    viewRole?: string; // DEPRECATED: Use isVisible instead
    cellColor?: Array<{condition: (rowData: any) => boolean; color: string}>; // Adds a background color to the cell based on condition
    maxWidth?: number; // Optional cap on the auto-calculated column width (pixels)
    calculationConfig?: {
        formula: string; // arithmetic expression using column names, e.g. "(base_trucking + fuel_surcharge) / total_gross_barrels"
        labels?: Record<string, string>; // optional human-readable labels for column refs shown in the tooltip
        valueType?: 'number' | 'accounting'; // how to format component values in the tooltip (default: 'number')
    };
}

// interfaces to define the structure of table json columns (if any)
export interface JsonColumnConfig {
    pointsTo: string; // Which table column this JSON config applies to
    fields?: JsonFieldConfig[]; // Array of field configurations
    displayJsonCols?: TableColumnConfig[]; // Which columns to display in the side panel
    SQLTable?: Omit<SQLTableProps, 'tableData'>; // if we're actually displaying another table
    tableInput?: InputColumnConfig[]; // configures the table input for sidepanel
    panelHeader?: (rowData: any) => string; // header for side panel
}

export interface JsonFieldConfig {
    colName: string; // json field name
    colType: 'text' | 'number' | 'boolean';
    placeholder?: string;
    required?: boolean;
    inputType?: 'input' | 'dropdown' | 'typeahead';
    dropdownOptions?: Array<{ value: any; label: string }>;
}

export interface TableResetActions {
    clearAllFilters: () => void;
    clearAllSorts: () => void;
}

const SQLTable: React.FC<SQLTableProps> = ({
    tableType,
    tableData,
    tableName,
    idColumn,
    columnConfigs,
    jsonColumnConfig,
    showHeader = true,
    tableTools = true,
    enableFilters = true,
    downloadable = true,
    exportColumnOrder,
    enableSettings = false,
    enableWarnings = false,
    enableColumnConfiguration = false,
    warningRules = [],
    sortBy,
    sortOrder,
    enableSummary = false,
    summaryConfig = [],
    // utility parameters
    preFilterData,
    persistFilters = false,
    initialFilter,
    monthNavigation= false,
    selectableRows = false,
    multipleSelection = false,
    deletableRows = false,
    deletionRequiresApproval = false,
    refreshable = true,
    userRoleLevel = 1, // default to viewer
    userData,
    onRowClick,
    sidepanelContent,
    sidepanelWidth = 480,
    // style parameters
    pageSize = 0,
    initialMonthYear,
    rowHeight = 30,
    rowRibbon,
    rowColor,
    showTable,
    // row actions
    rowActions,
    rowActionsPosition = 'end',
    rowActionsWidth = 120,
    skipDataFetch = false,
}) => {

    const { requestTableData, hasTableData, isTableLoading } = useDataManager();
    const { getMonthSelection } = useMonthNavigation();
    
    const [hasSelectedRows, setHasSelectedRows] = useState(false);
    const [selectedRowIndices, setSelectedRowIndices] = useState<number[]>([]);
    const [currentMonthFilter, setCurrentMonthFilter] = useState<string>('');
    const resetActionsRef = useRef<TableResetActions | null>(null);
    
    // Persistence key: route + table name, so the same table on two pages stays separate
    const persistKey = useMemo(
        () => `${window.location.pathname}::${tableName}`,
        [tableName]
    );
    // Read once on mount. An explicit initialFilter (navigation intent) beats saved filters.
    const [initialState] = useState(() => {
        const saved = persistFilters ? getSavedTableState(persistKey) : undefined;
        const filters: Record<string, Set<string>> =
            initialFilter && initialFilter.value != null
                ? { [initialFilter.column]: new Set([String(initialFilter.value)]) }
                : saved?.filters ?? {};
        const sorts: Record<string, 'asc' | 'desc' | 'none'> = saved?.sorts ?? {};
        return { filters, sorts };
    });

    const [activeFilters, setActiveFilters] = useState<Record<string, Set<string>>>(initialState.filters);
    const [activeSorts, setActiveSorts] = useState<Record<string, 'asc' | 'desc' | 'none'>>(initialState.sorts);
    const [hasActiveFilters, setHasActiveFilters] = useState(() => Object.keys(initialState.filters).length > 0);
    const [hasActiveSorts, setHasActiveSorts] = useState(() => Object.values(initialState.sorts).some(s => s !== 'none'));
    // Write-through: keeps the store in sync, including clears (which set {})
    useEffect(() => {
        if (!persistFilters) return;
        saveTableState(persistKey, { filters: activeFilters, sorts: activeSorts });
    }, [persistFilters, persistKey, activeFilters, activeSorts]);

    // Identifier only, not bound functions — the live filterFunction/groupKeyFunction
    // are re-derived from the current warningData every render (see
    // activeWarningFilterFns below), so the active filter can't go stale when the
    // underlying table data changes while it's applied.
    const [warningFilter, setWarningFilter] = useState<{
        key?: string;
        description?: string;
        type?: WarningTypes;
    }>({});
    const { userDetails, apiData } = useApiData();
    const [userVisibleColumns, setUserVisibleColumns] = useState<string[] | null>(null);

    type ActivePanel =
        | { kind: 'cell'; rowID: number; colName: string }
        | { kind: 'columnConfiguration' };

    const [activePanel, setActivePanel] = useState<ActivePanel | null>(null);

    const activePanelCell =
        activePanel?.kind === 'cell' ? activePanel : null;

    // Check if we have data for this table, if not request it
    useEffect(() => {
        if (skipDataFetch) return;
        const currentMonth = getHoustonDateInfo().currentMonthName
        const currentYear = getHoustonDateInfo().currentYear
        const currentMonthYear = `${String(currentMonth)} ${currentYear}`;
        // if monthNavigation is true, get default value from child component and use it to filter 
        if (monthNavigation) {
            // Prefer the prop (avoids child-before-parent useEffect timing issue),
            // then context (restored from previous navigation), then current month.
            const savedMonth = getMonthSelection(tableName);
            const targetMonth = initialMonthYear || savedMonth || currentMonthYear;
            if (!hasTableData(tableName, targetMonth)) {
                requestTableData(tableName, targetMonth);
            }
        }
        else if (!hasTableData(tableName)) {
            requestTableData(tableName);
        }
    }, [tableName, initialMonthYear]);

    const availableColumnConfigs = useMemo(() =>
        columnConfigs.filter(column =>
            (column.colName !== 'id' ||
                ['admin', 'super_admin'].includes(userDetails?.role || '')) &&
            (!column.isVisible || column.isVisible(userData))
        ),
        [columnConfigs, userDetails?.role, userData]
    );

    const presetColumnOrder = useMemo<string[] | null>(() => {
        const savedId = userDetails?.table_presets?.[tableName];
        if (savedId == null) return null;

        const preset = apiData?.table_presets?.find(preset =>
            String(preset.id) === String(savedId) &&
            preset.table_name === tableName &&
            (Number(preset.is_public) === 1 || preset.userId === userDetails?.id)
        );
        if (!preset) return null;

        try {
            const config: unknown = typeof preset.column_config === 'string'
                ? JSON.parse(preset.column_config)
                : preset.column_config;

            if (!Array.isArray(config) ||
                !config.every(value => typeof value === 'string')) return null;

            return Array.from(new Set(config as string[]));
        } catch {
            return null;
        }
    }, [apiData?.table_presets, userDetails, tableName]);

    const effectiveColumnConfigs = useMemo(() => {
        if (presetColumnOrder !== null) {
            const byName = new Map(
                availableColumnConfigs.map(column => [column.colName, column])
            );
            return presetColumnOrder.flatMap(colName => {
                const column = byName.get(colName);
                return column ? [column] : [];
            });
        }

        if (userVisibleColumns && userVisibleColumns.length > 0) {
            return availableColumnConfigs.filter(column =>
                userVisibleColumns.includes(column.colName)
            );
        }
        return availableColumnConfigs;
    }, [availableColumnConfigs, presetColumnOrder, userVisibleColumns]);

    // ALL FILTERING LOGIC
    // Month changes from MonthNavigation
    const handleMonthChange = useCallback((monthYear: string) => {
        setCurrentMonthFilter(monthYear);
    }, [tableData]);
    // Column filter changes from TableHeader
    const handleFiltersChange = useCallback((filters: Record<string, Set<string>>) => {
        setActiveFilters(filters);
        
    }, []);
    // Warning filter
    const handleWarningFilter = (key: string, description: string, type: WarningTypes) => {
        setWarningFilter({ key, description, type });
    };

    // useEffect to pre-filter table when navigating from outside
    useEffect(() => {
        if (initialFilter && initialFilter.value != null) {
            setActiveFilters({
                [initialFilter.column]: new Set([String(initialFilter.value)])
            });
            setHasActiveFilters(true);
        }
    }, [initialFilter]);

    // Handle clear all filters
    const handleClearAllFilters = useCallback(() => {
        resetActionsRef.current?.clearAllFilters();
        setActiveFilters({});
        setHasActiveFilters(false);
        setWarningFilter({});
    }, []);
    
    // Handle clear all sorts
    const handleClearAllSorts = useCallback(() => {
        resetActionsRef.current?.clearAllSorts();
        setActiveSorts({});
        setHasActiveSorts(false);
    }, []);
    
    // Handle filter state changes
    const handleFilterStateChange = useCallback((hasFilters: boolean) => {
        setHasActiveFilters(hasFilters);
    }, []);

    // Handle sort changes from TableHeader
    const handleSortsChange = useCallback((sorts: Record<string, 'asc' | 'desc' | 'none'>) => {
        setActiveSorts(sorts);
        // Update hasActiveSorts based on whether there are any active sorts
        const hasActiveSorting = Object.keys(sorts).length > 0 && 
                                Object.values(sorts).some(sortOrder => sortOrder !== 'none');
        setHasActiveSorts(hasActiveSorting);
    }, []);
    
    // Handle selection changes from TableRow
    const handleRowSelectionChange = useCallback((selectedRows: number[], hasSelected: boolean) => {
        setHasSelectedRows(hasSelected);
        setSelectedRowIndices(selectedRows);
    }, []);

    // Handle unselect all from header
    const handleUnselectAll = () => {
        // Call the unselect function exposed by TableRow
        if ((window as any).unselectAllRows) {
            (window as any).unselectAllRows();
        }
        setHasSelectedRows(false);
        setSelectedRowIndices([]);
    };

    // Function to handle panel toggle from cells
    const handlePanelToggle = useCallback((rowID: number, colName: string) => {
        // If the same panel is clicked, close it. Otherwise, switch to the new panel
        setActivePanel(current => {
            if (
                current?.kind === 'cell' &&
                current.rowID === rowID &&
                current.colName === colName
            ) {
                return null; // Close if same panel
            }
            return { kind: 'cell', rowID, colName }; // Open new panel (automatically closes others)
        });
    }, []);

    const handleOpenColumnConfiguration = useCallback(() => {
        setActivePanel(current =>
            current?.kind === 'columnConfiguration'
                ? null
                : { kind: 'columnConfiguration' }
        );
    }, []);

    // Function to close all panels
    const handleClosePanel = useCallback(() => {
        setActivePanel(null);
    }, []);

    const monthFilteredData = useMemo(() => {
        return applyMonthFilter(
            tableData,
            {
                monthFilter: currentMonthFilter,
                monthNavigation,
            }
        );
    }, [tableData, currentMonthFilter]);

    const preFilteredData = useMemo(() => {
        if (!preFilterData || !preFilterData.column || preFilterData.value == null) {
            return monthFilteredData;
        }
        
        // Permanently filter out data based on preFilterData
        return monthFilteredData.filter(row => {
            const cellValue = row[preFilterData.column];
            return String(cellValue) === String(preFilterData.value);
        });
    }, [monthFilteredData, preFilterData]);

    const sortedTableData = useMemo(() => {
        return applySorting(preFilteredData, {
            activeSorts,
            sortBy,
            sortOrder
        });
    }, [preFilteredData, activeSorts, sortOrder]);

    // gets warning data for table tools component
    const warningData = useMemo(() => {
        return evaluateWarningRules(sortedTableData, {
            enableWarnings,
            warningRules,
            idColumn,
            columnConfigs
        });
    }, [sortedTableData]);

    // Re-derives the active filter's filterFunction/groupKeyFunction from the CURRENT
    // warningData every time it recomputes (i.e. whenever sortedTableData changes),
    // instead of using a closure captured once at click-time. Without this, a
    // cross-row rule (e.g. the duplicate-ticket check) would keep evaluating against
    // an increasingly stale row snapshot while its filter stays active.
    const activeWarningFilterFns = useMemo(() => {
        if (!warningFilter.key || !warningFilter.type) return { filterFunction: undefined, groupKeyFunction: undefined };
        const byType = {
            warning: warningData.warningsByRule,
            alert: warningData.alertsByRule,
            pdf_alert: warningData.pdfAlertsByRule,
            suppressed: warningData.suppressedByRule,
        } as const;
        const match = byType[warningFilter.type]?.find(item => item.key === warningFilter.key);
        // No match (e.g. the last affected row was just resolved, so the rule no longer
        // has any affected rows) falls back to undefined — applyWarningFilters already
        // treats a missing filterFunction as "show everything," an acceptable edge case.
        return { filterFunction: match?.filterFunction, groupKeyFunction: match?.groupKeyFunction };
    }, [warningData, warningFilter.key, warningFilter.type]);

    const warningFilteredData = useMemo(() => {
        return applyWarningFilters(
            sortedTableData,
            {
                warningFilterFunction: activeWarningFilterFns.filterFunction,
                warningGroupKeyFunction: activeWarningFilterFns.groupKeyFunction
            }
        );
    }, [sortedTableData, activeWarningFilterFns]);

    const finalTableData = useMemo(() => {
        return applyColumnFilters(warningFilteredData, {
            activeFilters,
            columnConfigs,
            userData
        });
    }, [warningFilteredData, activeFilters, userData]);

    // Clear activePanelData if the open row is filtered out or removed
    // useEffect(() => {
    //     if (activePanelData == null) return;
    //     const stillExists = finalTableData.some(r => r[idColumn] === activePanelData.rowId);
    //     if (!stillExists) {
    //         setActivePanelData(null);
    //     }
    // }, [finalTableData, activePanelData, idColumn]);

    // Internal pagination — operates on post-filter data so filters always work on 100% of input
    const [tablePage, setTablePage] = useState(1);
    useEffect(() => { setTablePage(1); }, [finalTableData]);
    const totalTablePages = pageSize > 0 ? Math.max(1, Math.ceil(finalTableData.length / pageSize)) : 1;
    const paginatedData = useMemo(() => {
        if (pageSize <= 0) return finalTableData;
        const start = (tablePage - 1) * pageSize;
        return finalTableData.slice(start, start + pageSize);
    }, [finalTableData, tablePage, pageSize]);

    // Fixed width for icons
    const checkboxColumnWidth = 40;
    const filterIconWidth = 16;

    // Function to calculate max character count for each column
    const getMaxCharCountPerColumn = (tableData: Record<string, any>[], config: TableColumnConfig[]): number[] => {
        if (!config?.length) return [];
        
        return config.map(columnConfig => {
            // Calculate the width needed for the header text
            const headerText = columnConfig.colHeader || columnConfig.colName;
            const headerWidth = enableFilters ? headerText.length * 9 + filterIconWidth + 15 : headerText.length * 9;
            
            // If no data, just use header width
            if (!tableData?.length) {
                if (columnConfig.colType === 'json') {
                    return Math.max(100, headerWidth);
                }
                return headerWidth;
            }
            
            let dataWidth = 0;
            // Calculate the width needed for the column data
            dataWidth = Math.max(...tableData.map(row => {
                const cellValue = String(row[columnConfig.colName] || '');
                
                // Use different multipliers for different character types
                let estimatedWidth = 0;
                for (let char of cellValue) {
                    if (/\d/.test(char)) {
                        estimatedWidth += 12; // Numbers are wider
                    } else if (/[A-Z]/.test(char)) {
                        estimatedWidth += 9; // Uppercase letters
                    } else if (/[a-z]/.test(char)) {
                        estimatedWidth += 8; // Lowercase letters
                    } else {
                        estimatedWidth += 7; // Other characters (punctuation, etc.)
                    }
                }
                
                return estimatedWidth;
            }));
            
            if (dataWidth > 400) dataWidth = 400; // cap at 400px max

            if (columnConfig.inputType === 'dropdown') {
                dataWidth += 20; // extra space for dropdown arrow
            }

            if (columnConfig.enableSidepanel) {
                dataWidth += 30; // extra space for sidepanel button
            }

            // Use the larger of header width or data width
            const baseWidth = Math.max(headerWidth, dataWidth);

            // on json columns make sure to have a minimum width of 100
            if (columnConfig.colType === 'json') {
                return Math.max(100, headerWidth);
            }

            // Respect optional maxWidth cap
            if (columnConfig.maxWidth && baseWidth > columnConfig.maxWidth) {
                return columnConfig.maxWidth;
            }

            return baseWidth;
        });
    };

    const columnWidths = useMemo(() => 
        getMaxCharCountPerColumn(tableData, effectiveColumnConfigs),
        [tableData, effectiveColumnConfigs]
    );
    const minWidth = 50; // Minimum col width parameter

    // Calculate total table width including checkbox column and actions column
    const dataColumnsWidth = columnWidths.reduce((sum, count) => {
        return sum + (count < minWidth ? minWidth : count);
    }, 0);
    
    // const liveRow = useMemo(
    //     () => activePanelData ? finalTableData.find(r => r[idColumn] === activePanelData.rowId) ?? null : null,
    //     [finalTableData, activePanelData, idColumn]
    // );

    let totalWidth = dataColumnsWidth;
    if (selectableRows) totalWidth += checkboxColumnWidth;
    if (rowActions && rowActions.length > 0) totalWidth += rowActionsWidth;

    useEffect(() => {
        if (!showTable) return;

        trackEvent('table_view', {
            tableName: tableName,
        });
    }, [tableName, showTable]);

    if (!showTable) {
        return <p>Not allowed</p>
    }

    return (
        <div 
            className='tableMain-mainContainer'
            style={{maxWidth: activePanel ? 'calc(100vw - 550px)' : '100vw'}} // leave space for side panel when open
        >
            <div className='tableMain-mainContainer'>
                <div className="tableMain-flexContainer">
                    {pageSize > 0 && totalTablePages > 1 && (
                        <div className="tableMain-pagination">
                            <button
                                className="tableMain-page-btn"
                                onClick={() => setTablePage(p => Math.max(1, p - 1))}
                                disabled={tablePage === 1}
                            ><ArrowLeft size={16} /></button>
                            <span className="tableMain-page-info">
                                Page {tablePage} of {totalTablePages}
                            </span>
                            <button
                                className="tableMain-page-btn"
                                onClick={() => setTablePage(p => Math.min(totalTablePages, p + 1))}
                                disabled={tablePage >= totalTablePages}
                            ><ArrowRight size={16} /></button>
                            <span className="tableMain-page-records">
                                {finalTableData.length.toLocaleString()} records
                            </span>
                        </div>
                    )}
                    {tableTools && (
                        <TableTools
                            finalTableData={finalTableData}
                            fullColumnConfigs={columnConfigs}
                            effectiveColumnConfigs={effectiveColumnConfigs}
                            jsonColumnConfig={jsonColumnConfig}
                            selectable={selectableRows}
                            hasSelectedRows={hasSelectedRows}
                            selectedRowIndices={selectedRowIndices}
                            tableName={tableName}
                            onClearSelection={handleUnselectAll}
                            deletable={deletableRows}
                            deletionRequiresApproval={deletionRequiresApproval}
                            downloadable={downloadable}
                            exportColumnOrder={presetColumnOrder !== null ? undefined : exportColumnOrder}
                            refreshable={refreshable}
                            enableSettings={enableSettings}
                            enableColumnConfiguration={enableColumnConfiguration}
                            hasActiveColumnPreset={presetColumnOrder !== null}
                            onVisibleColumnsChange={setUserVisibleColumns}
                            onOpenColumnConfiguration={handleOpenColumnConfiguration}
                            isColumnConfigurationOpen={activePanel?.kind === 'columnConfiguration'}
                            enableWarnings={enableWarnings}
                            warningData={warningData}
                            onClearFilters={handleClearAllFilters}
                            hasActiveFilters={hasActiveFilters}
                            onClearSorts={handleClearAllSorts}
                            hasActiveSorts={hasActiveSorts}
                            onWarningFilter={handleWarningFilter}
                            warningFilter={warningFilter}
                            monthNavigation={monthNavigation}
                            initialMonthYear={initialMonthYear}
                            onMonthChange={handleMonthChange}
                            rowColor={rowColor}
                            userData={userData}
                        />
                    )}
                    <div className="tableMain-horizontalScrollContainer">
                        <TableBody
                            tableType={tableType}
                            finalTableData={paginatedData}
                            tableName={tableName}
                            idColumn={idColumn}
                            columnConfigs={effectiveColumnConfigs}
                            jsonColumnConfig={jsonColumnConfig}

                            showHeader={showHeader}
                            selectableRows={selectableRows}
                            multipleSelection={multipleSelection}
                            userRoleLevel={userRoleLevel}
                            userData={userData}
                            isLoading={isTableLoading(tableName)}

                            enableFilters={enableFilters}
                            hasSelectedRows={hasSelectedRows}
                            onSelectAll={handleUnselectAll}
                            dataForHeader={warningFilteredData}
                            initialFilters={activeFilters}
                            initialSorts={initialState.sorts}

                            onRowSelectionChange={handleRowSelectionChange}
                            onFilterStateChange={handleFilterStateChange}
                            onFiltersChange={handleFiltersChange}
                            onSortsChange={handleSortsChange}
                            onRowClick={onRowClick}
                            resetActionsRef={resetActionsRef}

                            warningsByRow={warningData.warningsByRow}
                            ignoredActiveWarningsByRow={warningData.ignoredActiveWarningsByRow}

                            rowHeight={rowHeight}
                            columnWidths={columnWidths}
                            totalWidth={totalWidth}
                            minWidth={minWidth}
                            checkboxColumnWidth={checkboxColumnWidth}
                            rowRibbon={rowRibbon}
                            rowColor={rowColor}
                            rowActions={rowActions}
                            rowActionsPosition={rowActionsPosition}
                            actionsColumnWidth={rowActionsWidth}

                            // Side panel props
                            activePanelCell={activePanelCell}
                            onPanelToggle={handlePanelToggle}
                            onPanelClose={handleClosePanel}
                        />
                    </div>
                    {enableSummary && (
                        <TableSummary
                            data={finalTableData}
                            activeFilters={activeFilters}
                            summaryConfig={summaryConfig}
                            userData={userData}
                        />
                    )}
                </div>
                {/* Side Panel Portal */}
                {activePanel && (
                    <TableSidePanel
                        tableType={tableType}
                        tableName={activePanel.kind === 'cell' ? tableName : 'custom_column'}
                        sourceTableName={tableName}
                        isOpen={true}
                        onClose={handleClosePanel}
                        columnConfigs={availableColumnConfigs}
                        panelData={(() => {
                            if (activePanel.kind !== 'cell') return undefined;
                            // Find row data for the active panel from finalTableData
                            const rowData = finalTableData.find(
                                row => row[idColumn] === activePanel.rowID
                            );
                            return rowData ? {
                                rowData,
                                colName: activePanel.colName,
                                tableName
                            } : undefined;
                        })()}
                    />
                )}
            </div>
        </div>
    );
};

export default SQLTable;
